import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import Layout from "../components/Layout";
import images from "../common/images";
import Hero from "../components/Hero";
import Card from "../components/Card";
import LoadingSpinner from "../components/LoadingSpinner";
import { useSelector } from "react-redux";
import CityDropDown from "../components/CityDropDown";
import useUserLocation from "../hooks/useUserLocation";
import { filterItemsWithinRadius, sortByLocation } from "../utils/locationSort";
import { buildAiSearchParams, isNearbySearchQuery } from "../utils/searchLocation";
import ApiCall from "../services/apiCall";
import AnimatedSearchBar from "../components/AnimatedSearchBar";

const useQ = () => {
  return new URLSearchParams(useLocation().search);
};

const searchTypeImages = [
  {
    id: 1,
    label: "Area",
    image: images.areaImage,
  },
  {
    id: 2,
    label: "Genre",
    image: images.genreImage,
  },
  {
    id: 3,
    label: "Artists",
    image: images.artistImage,
  },
  {
    id: 4,
    label: "Clubs",
    image: images.clubImage,
  },
];

const buildTrendingSearchPrompts = (city = "your city") => [
  `Rooftop clubs this weekend in ${city}`,
  `Bollywood night events in ${city}`,
  `Live music events near me`,
  `Trending artists for parties in ${city}`,
  `Clubs with happy hours in ${city}`,
  `Events by area or genre in ${city}`,
  `Lounges for birthday parties`,
  `EDM and techno nights in ${city}`,
  `Weekend events with music in ${city}`,
  `Book DJs and singers in ${city}`,
  `Best restaurants in ${city}`,
  `Rooftop restaurants in ${city}`,
  `Restaurants with live music in ${city}`,
  `Fine dining near me`,
  `Places to eat before the club in ${city}`,
];

const SEARCH_RESULT_TYPES = ["club", "artist", "event"];
const SEARCH_RESULT_DISPLAY_ORDER = ["event", "artist", "club"];

const normalizeSearchType = (type) => {
  const normalized = `${type ?? ""}`.toLowerCase().trim();

  if (["club", "clubs", "restaurant", "restaurants", "bar", "bars", "lounge", "lounges"].includes(normalized)) {
    return "club";
  }

  if (["artist", "artists", "dj", "djs", "singer", "singers", "band", "bands"].includes(normalized)) {
    return "artist";
  }

  if (["event", "events", "party", "parties", "gig", "gigs", "night", "nights"].includes(normalized)) {
    return "event";
  }

  return null;
};

const getUniqueSearchTypes = (types = []) => {
  const seen = new Set();

  return types.reduce((result, type) => {
    const normalized = normalizeSearchType(type);

    if (normalized && !seen.has(normalized)) {
      seen.add(normalized);
      result.push(normalized);
    }

    return result;
  }, []);
};

const inferSearchTypesFromQuery = (query = "") => {
  const text = query.toLowerCase();
  const matches = [
    {
      type: "artist",
      match: text.match(/\b(artist|artists|dj|djs|singer|singers|band|bands|performer|performers)\b/),
    },
    {
      type: "club",
      match: text.match(/\b(club|clubs|restaurant|restaurants|bar|bars|lounge|lounges|rooftop|happy hour|happy hours|budget|cheap|affordable|fine dining)\b/),
    },
    {
      type: "event",
      match: text.match(/\b(event|events|party|parties|gig|gigs|night|nights|weekend|tonight|live music|bollywood night|edm|techno)\b/),
    },
  ];

  return getUniqueSearchTypes(
    matches
      .filter(({ match }) => match)
      .sort((a, b) => a.match.index - b.match.index)
      .map(({ type }) => type)
  );
};

const getPriceIntentFromQuery = (query = "") => {
  const text = query.toLowerCase();
  const underMatch = text.match(/\b(?:under|below|less than|within|max|maximum|upto|up to)\s*(?:rs\.?|₹|inr)?\s*(\d{2,6})\b/i);
  const betweenMatch = text.match(/\bbetween\s*(?:rs\.?|₹|inr)?\s*(\d{2,6})\s*(?:and|-|to)\s*(?:rs\.?|₹|inr)?\s*(\d{2,6})\b/i);

  if (betweenMatch) {
    const firstPrice = Number(betweenMatch[1]);
    const secondPrice = Number(betweenMatch[2]);

    if (Number.isFinite(firstPrice) && Number.isFinite(secondPrice)) {
      return {
        minPrice: Math.min(firstPrice, secondPrice),
        maxPrice: Math.max(firstPrice, secondPrice),
      };
    }
  }

  if (underMatch) {
    const maxPrice = Number(underMatch[1]);

    if (Number.isFinite(maxPrice)) {
      return {
        minPrice: null,
        maxPrice,
      };
    }
  }

  return {
    minPrice: null,
    maxPrice: null,
  };
};

const getOptionalNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const getSearchIntent = ({ query, responsePayload, fallbackPriority = ["event", "artist", "club"] }) => {
  const queryTypes = inferSearchTypesFromQuery(query);
  const entityTypes = getUniqueSearchTypes(responsePayload?.entities);
  const apiPriority = getUniqueSearchTypes(responsePayload?.resultsPriority);
  const priceIntent = getPriceIntentFromQuery(query);
  const isBudgetQuery = /\b(budget|cheap|affordable|low cost|low-cost|pocket friendly|pocket-friendly)\b/i.test(query);
  const isBestQuery = /\b(best|good|top|popular|trending|recommended|recommend)\b/i.test(query);
  // Only treat query keywords as an explicit type hint for the API request.
  // Free-text club names (e.g. "Kitty Su") should not be forced to entities=event.
  const hasExplicitType = queryTypes.length > 0;
  const isNameLikeQuery =
    !hasExplicitType &&
    !isBudgetQuery &&
    !isBestQuery &&
    !isNearbySearchQuery(query);
  const priority = getUniqueSearchTypes([
    ...entityTypes,
    ...queryTypes,
    ...apiPriority,
    ...fallbackPriority,
    ...SEARCH_RESULT_TYPES,
  ]);

  return {
    priority,
    primaryType: priority[0] || "event",
    hasExplicitType,
    isNameLikeQuery,
    sortBy: isBudgetQuery ? "budget" : isBestQuery ? "best" : null,
    maxPrice: getOptionalNumber(responsePayload?.maxPrice) ?? priceIntent.maxPrice,
    minPrice: getOptionalNumber(responsePayload?.minPrice) ?? priceIntent.minPrice,
    isBudgetQuery,
    isBestQuery,
  };
};

const buildIntentAwareSearchRequest = (baseRequest, intent) => {
  const params = new URLSearchParams(baseRequest.params.toString());

  // Always search clubs, artists, and events together. Never restrict to one type.
  params.set("entities", SEARCH_RESULT_TYPES.join("|"));
  params.set("resultTypes", SEARCH_RESULT_TYPES.join("|"));

  if (intent?.sortBy) {
    params.set("sortBy", intent.sortBy);
  }

  if (intent?.minPrice !== null && intent?.minPrice !== undefined) {
    params.set("minPrice", String(intent.minPrice));
  }

  if (intent?.maxPrice !== null && intent?.maxPrice !== undefined) {
    params.set("maxPrice", String(intent.maxPrice));
  }

  return {
    ...baseRequest,
    params,
    searchIntent: intent,
  };
};

const hasResultArrays = (value) =>
  Boolean(
    value &&
      (Array.isArray(value.clubs) ||
        Array.isArray(value.artists) ||
        Array.isArray(value.events))
  );

const resolveSearchResponsePayload = (searchData) => {
  const candidates = [
    searchData?.results,
    searchData?.data?.results,
    searchData?.data?.data?.results,
    searchData?.data,
    searchData?.data?.data,
    searchData,
  ];

  return candidates.find(hasResultArrays) || {};
};

const resolveSearchMetadataPayload = (searchData, responsePayload) => ({
  ...(searchData || {}),
  ...(searchData?.data || {}),
  ...(searchData?.data?.data || {}),
  ...(responsePayload || {}),
});

const getComparablePrice = (item, type) => {
  if (!item) return null;

  const rawPrice =
    type === "event"
      ? item?.price?.amount ?? item?.amount ?? item?.cost
      : item?.cost ?? item?.price?.amount ?? item?.price;
  const price = Number(rawPrice);

  return Number.isFinite(price) ? price : null;
};

const SEARCH_STOP_WORDS = new Set([
  "in",
  "the",
  "a",
  "an",
  "and",
  "or",
  "of",
  "for",
  "to",
  "at",
  "on",
  "me",
  "near",
  "nearby",
  "with",
]);

const getNameSearchTokens = (query = "", extraStopWords = []) => {
  const stopWords = new Set([
    ...SEARCH_STOP_WORDS,
    ...extraStopWords.map((word) => `${word ?? ""}`.toLowerCase().trim()).filter(Boolean),
  ]);

  return `${query}`
    .toLowerCase()
    .split(/[^a-z0-9]+/i)
    .map((token) => token.trim())
    .filter((token) => token.length >= 2 && !stopWords.has(token));
};

const SEARCH_TEXT_KEYS = [
  "name",
  "title",
  "clubName",
  "displayName",
  "slug",
  "locality",
  "city",
  "musicGenre",
  "genre",
  "type",
  "description",
  "tags",
  "keywords",
  "aliases",
  "artists",
  "club",
  "clubId",
];

const collectSearchText = (value, parts = [], depth = 0) => {
  if (value == null || value === "" || depth > 3) return parts;

  if (Array.isArray(value)) {
    value.slice(0, 25).forEach((entry) => collectSearchText(entry, parts, depth + 1));
    return parts;
  }

  if (typeof value === "object") {
    SEARCH_TEXT_KEYS.forEach((key) => collectSearchText(value[key], parts, depth + 1));
    return parts;
  }

  parts.push(String(value));
  return parts;
};

const getItemSearchText = (item) => collectSearchText(item).join(" ").toLowerCase();

const itemMatchesNameQuery = (item, query, extraStopWords = []) => {
  const tokens = getNameSearchTokens(query, extraStopWords);
  if (!tokens.length) return true;

  const haystack = getItemSearchText(item);
  if (!haystack) return true;

  return tokens.every((token) => haystack.includes(token));
};

const unwrapSearchItem = (item) => {
  if (!item || typeof item !== "object" || !item._doc) return item;

  const { _doc, ...rest } = item;
  return {
    ..._doc,
    ...rest,
  };
};

const extractNamedSearchList = (response) => {
  const candidates = [
    response?.results?.clubs,
    response?.results?.artists,
    response?.results?.events,
    response?.data?.clubs,
    response?.data?.artists,
    response?.data?.events,
    response?.data?.data,
    response?.data,
    response?.results,
    response?.clubs,
    response?.artists,
    response?.events,
  ];

  return candidates.find(Array.isArray) || [];
};

const mergeUniqueSearchItems = (...lists) => {
  const seen = new Set();

  return lists.flat().reduce((result, item) => {
    const unwrapped = unwrapSearchItem(item);
    const key = `${unwrapped?._id || unwrapped?.id || unwrapped?.name || unwrapped?.title || ""}`;
    if (!key || seen.has(key)) return result;

    seen.add(key);
    result.push(unwrapped);
    return result;
  }, []);
};

const getEventDateValue = (event) => {
  const rawDate =
    event?.eventEndTime ||
    event?.endTime ||
    event?.endDate ||
    event?.eventStartTime ||
    event?.startTime ||
    event?.date ||
    event?.startDate;

  if (!rawDate) return null;

  const parsed = new Date(rawDate);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isCurrentOrUpcomingEvent = (event) => {
  const eventDate = getEventDateValue(event);
  if (!eventDate) return true;

  const start = event?.eventStartTime || event?.startTime || event?.date;
  const end = event?.eventEndTime || event?.endTime || event?.endDate;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const endDate = end ? new Date(end) : null;
  const startDate = start ? new Date(start) : eventDate;

  if (endDate && !Number.isNaN(endDate.getTime())) {
    return endDate >= today;
  }

  if (startDate && !Number.isNaN(startDate.getTime())) {
    const startDay = new Date(startDate);
    startDay.setHours(0, 0, 0, 0);
    return startDay >= today;
  }

  return true;
};

const refineResultsByIntent = (items, type, intent, query, extraStopWords = []) => {
  if (!Array.isArray(items)) return [];

  const priceFilteredItems = items.filter((item) => {
    const price = getComparablePrice(item, type);

    if (intent.minPrice !== null && price !== null && price < intent.minPrice) {
      return false;
    }

    if (intent.maxPrice !== null && price !== null && price > intent.maxPrice) {
      return false;
    }

    if (
      intent.isNameLikeQuery &&
      !item.__relatedToSearch &&
      !itemMatchesNameQuery(item, query, extraStopWords)
    ) {
      return false;
    }

    return true;
  });

  if (!intent.isBudgetQuery || (type !== "club" && type !== "event")) {
    return priceFilteredItems;
  }

  return [...priceFilteredItems].sort((a, b) => {
    const priceA = getComparablePrice(a, type);
    const priceB = getComparablePrice(b, type);

    if (priceA === null && priceB === null) return 0;
    if (priceA === null) return 1;
    if (priceB === null) return -1;

    return priceA - priceB;
  });
};

const Search = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const navigate = useNavigate();
  const query = useQ();
  const searchKey = query.get("q");
  const includeResults = query.get("results");
  const [valuekey, setValuekey] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchStarting, setIsSearchStarting] = useState(false);

  useEffect(() => {
    setValuekey("");
  }, []);

  useEffect(() => {
    if (searchKey) {
      setValuekey(searchKey);
      setSearchQuery(searchKey);
    }
  }, [searchKey]);

  const selectedCity = useSelector((state) => state.citySelector.selectedCity);
  const userBaseCity = useSelector((state) => state.citySelector.userBaseCity);
  const isManualCitySelection = useSelector(
    (state) => state.citySelector.isManualSelection
  );
  const selectedTrainStation = useSelector((state) => state.trainStation.selectedStation);

  const trendingPromptCity = selectedCity || userBaseCity || "your city";
  const trendingPrompts = useMemo(
    () => buildTrendingSearchPrompts(trendingPromptCity),
    [trendingPromptCity]
  );

  const {
    locationData,
    coords,
    locationStatus,
    error: locationError,
    nearestTrainStation,
    refreshLocation,
    loading: locationLoading,
    permissionState,
  } = useUserLocation();

  const trimmedSearchKey = `${searchQuery ?? ""}`.trim();
  const isNearbyQuery = isNearbySearchQuery(trimmedSearchKey);
  const hasLiveCoordinates =
    Number.isFinite(coords?.latitude) && Number.isFinite(coords?.longitude);
  const requestSearchIntent = useMemo(
    () =>
      getSearchIntent({
        query: trimmedSearchKey,
        responsePayload: {},
      }),
    [trimmedSearchKey]
  );
  const preferSelectedCity = false;
  const ignoreLiveLocation = !isNearbyQuery;

  const aiSearchRequest = useMemo(
    () => {
      const baseSearchRequest = buildAiSearchParams({
        query: trimmedSearchKey,
        selectedCity,
        userBaseCity,
        locationData,
        coords,
        trainStation: ignoreLiveLocation
          ? null
          : selectedTrainStation || nearestTrainStation,
        ignoreLiveLocation,
        preferSelectedCity,
        appendCityToQuery: false,
        includeCity: isNearbyQuery,
      });

      return buildIntentAwareSearchRequest(baseSearchRequest, requestSearchIntent);
    },
    [
      coords,
      ignoreLiveLocation,
      isNearbyQuery,
      locationData,
      nearestTrainStation,
      preferSelectedCity,
      requestSearchIntent,
      selectedCity,
      selectedTrainStation,
      trimmedSearchKey,
      userBaseCity,
    ]
  );
  const hasResolvedLocation =
    locationStatus === "denied" ||
    locationStatus === "unavailable" ||
    Boolean(locationData) ||
    Boolean(locationError);
  const needsLocationForNearby = isNearbyQuery && !hasLiveCoordinates;
  const canFetchSearch =
    !!trimmedSearchKey &&
    !needsLocationForNearby &&
    (isManualCitySelection || hasResolvedLocation || !isNearbyQuery);

  useEffect(() => {
    const latitude =
      Number.isFinite(coords?.latitude)
        ? coords.latitude
        : Array.isArray(locationData?.coordinates)
          ? locationData.coordinates[1]
          : null;
    const longitude =
      Number.isFinite(coords?.longitude)
        ? coords.longitude
        : Array.isArray(locationData?.coordinates)
          ? locationData.coordinates[0]
          : null;

    console.log("Search location:", {
      latitude,
      longitude,
      city: locationData?.city || selectedCity || null,
      locality: locationData?.locality || null,
      query: trimmedSearchKey || null,
    });
  }, [coords, locationData, selectedCity, trimmedSearchKey]);

  const {
    data: searchData,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: [
      "api/ai-search",
      aiSearchRequest.params.toString(),
      aiSearchRequest.usedLiveLocation ? aiSearchRequest.liveLocation : null,
    ],
    queryFn: async () => {
      const latitude =
        Number.isFinite(coords?.latitude)
          ? coords.latitude
          : Array.isArray(locationData?.coordinates)
            ? locationData.coordinates[1]
            : null;
      const longitude =
        Number.isFinite(coords?.longitude)
          ? coords.longitude
          : Array.isArray(locationData?.coordinates)
            ? locationData.coordinates[0]
            : null;

      console.log("AI search request location:", {
        latitude,
        longitude,
        city: locationData?.city || selectedCity || null,
        url: `api/ai-search?${aiSearchRequest.params.toString()}`,
      });

      const response = await ApiCall(
        `api/ai-search?${aiSearchRequest.params.toString()}`,
        "GET"
      );
      if (response?.ok === false) throw new Error("Failed to fetch search results");
      return response;
    },

    enabled: canFetchSearch,
    placeholderData: {
      data: {
        clubs: [],
        artists: [],
        events: [],
      },
    },
  });

  const nameSearchEnabled = canFetchSearch;

  const fetchNamedEntityList = async (entityPath) => {
    const params = new URLSearchParams();
    params.set("page", "0");
    params.set("q", trimmedSearchKey);
    if (entityPath === "events") {
      params.set("upcoming", "1");
    }

    const response = await ApiCall(`api/${entityPath}?${params.toString()}`, "GET");
    if (response?.ok === false) return [];
    return extractNamedSearchList(response);
  };

  const fetchRelatedEventsByIds = async (paramName, ids) => {
    const uniqueIds = [...new Set((ids || []).filter(Boolean))].slice(0, 5);

    const lists = await Promise.all(
      uniqueIds.map(async (id) => {
        const params = new URLSearchParams();
        params.set("upcoming", "1");
        params.set("page", "0");
        params.set(paramName, id);

        const response = await ApiCall(`api/events?${params.toString()}`, "GET");
        if (response?.ok === false) return [];
        return extractNamedSearchList(response);
      })
    );

    return lists.flat();
  };

  const { data: namedClubs = [], isFetching: isFetchingNamedClubs } = useQuery({
    queryKey: ["api/clubs", "name-search", trimmedSearchKey],
    queryFn: () => fetchNamedEntityList("clubs"),
    enabled: nameSearchEnabled,
    placeholderData: [],
  });

  const { data: namedArtists = [], isFetching: isFetchingNamedArtists } = useQuery({
    queryKey: ["api/artists", "name-search", trimmedSearchKey],
    queryFn: () => fetchNamedEntityList("artists"),
    enabled: nameSearchEnabled,
    placeholderData: [],
  });

  const { data: namedEvents = [], isFetching: isFetchingNamedEvents } = useQuery({
    queryKey: ["api/events", "name-search", trimmedSearchKey],
    queryFn: () => fetchNamedEntityList("events"),
    enabled: nameSearchEnabled,
    placeholderData: [],
  });

  useEffect(() => {
    if (!trimmedSearchKey || isFetching) {
      setIsSearchStarting(false);
    }
  }, [isFetching, trimmedSearchKey]);

  const applySearchQuery = (nextQuery, { refetchIfSame = false } = {}) => {
    const nextSearchQuery = `${nextQuery ?? ""}`.trim();
    if (!nextSearchQuery) return;

    setValuekey(nextSearchQuery);
    setSearchQuery(nextSearchQuery);
    setIsSearchStarting(true);

    const currentQuery = `${searchKey ?? ""}`.trim();
    if (nextSearchQuery === currentQuery) {
      if (refetchIfSame && canFetchSearch) {
        refetch();
      }
      return;
    }

    navigate(`/search?q=${encodeURIComponent(nextSearchQuery)}`);
  };

  const submitSearch = () => {
    applySearchQuery(valuekey, { refetchIfSame: true });
  };


  const recommendationCity = selectedCity || userBaseCity || "";

  const { data: recommendation } = useQuery({
    queryKey: ['api/recommendations', recommendationCity],
    queryFn: async () => {
      const response = await ApiCall(
        `api/recommendations?city=${encodeURIComponent(recommendationCity)}`,
        "GET"
      );
      if (response?.ok === false) throw new Error("Failed to fetch recommendations");
      return response;
    },
    enabled: !!recommendationCity,
    placeholderData: {
      data: {
        data: [],
      },
    },
  });

  const responsePayload = resolveSearchResponsePayload(searchData);
  const searchIntent = useMemo(
    () => {
      const searchIntentPayload = resolveSearchMetadataPayload(searchData, responsePayload);

      return getSearchIntent({
        query: trimmedSearchKey,
        responsePayload: searchIntentPayload,
      });
    },
    [responsePayload, searchData, trimmedSearchKey]
  );
  const namedMatchStopWords = [selectedCity, userBaseCity];
  const matchingNamedClubs = (namedClubs || []).filter((item) =>
    itemMatchesNameQuery(unwrapSearchItem(item), trimmedSearchKey, namedMatchStopWords)
  );
  const matchingNamedArtists = (namedArtists || []).filter((item) =>
    itemMatchesNameQuery(unwrapSearchItem(item), trimmedSearchKey, namedMatchStopWords)
  );
  const matchingNamedEvents = (namedEvents || []).filter((item) =>
    itemMatchesNameQuery(unwrapSearchItem(item), trimmedSearchKey, namedMatchStopWords)
  );

  const clubs = mergeUniqueSearchItems(
    matchingNamedClubs,
    (responsePayload?.clubs || []).map(unwrapSearchItem)
  );

  const artists = mergeUniqueSearchItems(
    matchingNamedArtists,
    (responsePayload?.artists || []).map(unwrapSearchItem)
  );

  const relatedArtistIds = useMemo(
    () =>
      [...new Set(
        artists
          .filter((artist) =>
            itemMatchesNameQuery(artist, trimmedSearchKey, [selectedCity, userBaseCity])
          )
          .map((artist) => artist?._id || artist?.id)
          .filter(Boolean)
      )],
    [artists, selectedCity, trimmedSearchKey, userBaseCity]
  );

  const relatedClubIds = useMemo(
    () =>
      [...new Set(
        clubs
          .filter((club) =>
            itemMatchesNameQuery(club, trimmedSearchKey, [selectedCity, userBaseCity])
          )
          .map((club) => club?._id || club?.id)
          .filter(Boolean)
      )],
    [clubs, selectedCity, trimmedSearchKey, userBaseCity]
  );

  const { data: relatedArtistEvents = [], isFetching: isFetchingRelatedArtistEvents } = useQuery({
    queryKey: ["api/events", "related-artists", relatedArtistIds],
    queryFn: () => fetchRelatedEventsByIds("artistId", relatedArtistIds),
    enabled: canFetchSearch && relatedArtistIds.length > 0,
    placeholderData: [],
  });

  const { data: relatedClubEvents = [], isFetching: isFetchingRelatedClubEvents } = useQuery({
    queryKey: ["api/events", "related-clubs", relatedClubIds],
    queryFn: () => fetchRelatedEventsByIds("clubId", relatedClubIds),
    enabled: canFetchSearch && relatedClubIds.length > 0,
    placeholderData: [],
  });

  const relatedEvents = mergeUniqueSearchItems(relatedArtistEvents, relatedClubEvents).map(
    (event) => ({
      ...event,
      __relatedToSearch: true,
    })
  );

  const events = mergeUniqueSearchItems(
    matchingNamedEvents,
    (responsePayload?.events || []).map(unwrapSearchItem),
    relatedEvents
  );

  const showSearchLoader =
    (isNearbyQuery && !hasLiveCoordinates && locationLoading) ||
    isFetching ||
    isSearchStarting ||
    isFetchingNamedClubs ||
    isFetchingNamedArtists ||
    isFetchingNamedEvents ||
    isFetchingRelatedArtistEvents ||
    isFetchingRelatedClubEvents;

  const userCoordinates = useMemo(() => {
    if (isManualCitySelection && !isNearbyQuery) {
      return null;
    }

    const sourceCoordinates = Array.isArray(locationData?.coordinates)
      ? locationData.coordinates
      : Number.isFinite(coords?.longitude) && Number.isFinite(coords?.latitude)
        ? [coords.longitude, coords.latitude]
        : null;

    if (!Array.isArray(sourceCoordinates)) {
      return null;
    }

    const [longitude, latitude] = sourceCoordinates;
    if (
      longitude === undefined ||
      latitude === undefined ||
      longitude === null ||
      latitude === null
    ) {
      return null;
    }

    return {
      latitude: Number(latitude),
      longitude: Number(longitude),
    };
  }, [coords, isManualCitySelection, isNearbyQuery, locationData]);

  const sortedClubs = useMemo(() => {
    if (!Array.isArray(clubs) || !clubs.length) return [];
    const validClubs = clubs.filter((c) => c?.name || c?.title);
    const nearbyClubs =
      isNearbyQuery && userCoordinates
        ? filterItemsWithinRadius(validClubs, userCoordinates)
        : validClubs;
    if (!nearbyClubs.length) return [];
    const locationSortedClubs = sortByLocation(nearbyClubs, {
      coordinates: userCoordinates,
      locality: isManualCitySelection ? null : locationData?.locality,
      city: isManualCitySelection ? selectedCity : locationData?.city || selectedCity,
    });
    return refineResultsByIntent(
      locationSortedClubs,
      "club",
      searchIntent,
      trimmedSearchKey,
      [selectedCity, userBaseCity]
    );
  }, [clubs, isManualCitySelection, isNearbyQuery, locationData?.city, locationData?.locality, searchIntent, selectedCity, trimmedSearchKey, userBaseCity, userCoordinates]);

  const sortedArtists = useMemo(() => {
    if (!Array.isArray(artists) || !artists.length) return [];
    const validArtists = artists.filter((a) => a?.name || a?.title);
    const nearbyArtists =
      isNearbyQuery && userCoordinates
        ? filterItemsWithinRadius(validArtists, userCoordinates)
        : validArtists;
    if (!nearbyArtists.length) return [];
    const locationSortedArtists = sortByLocation(nearbyArtists, {
      coordinates: userCoordinates,
      locality: isManualCitySelection ? null : locationData?.locality,
      city: isManualCitySelection ? selectedCity : locationData?.city || selectedCity,
    });
    return refineResultsByIntent(
      locationSortedArtists,
      "artist",
      searchIntent,
      trimmedSearchKey,
      [selectedCity, userBaseCity]
    );
  }, [artists, isManualCitySelection, isNearbyQuery, locationData?.city, locationData?.locality, searchIntent, selectedCity, trimmedSearchKey, userBaseCity, userCoordinates]);

  const sortedEvents = useMemo(() => {
    if (!Array.isArray(events) || !events.length) return [];
    const validEvents = events.filter(
      (e) => (e?.title || e?.name) && isCurrentOrUpcomingEvent(e)
    );
    const nearbyEvents =
      isNearbyQuery && userCoordinates
        ? filterItemsWithinRadius(validEvents, userCoordinates)
        : validEvents;
    if (!nearbyEvents.length) return [];
    const mappedEvents = nearbyEvents.map((event) => ({ ...event, type: "event" }));
    const locationSortedEvents = sortByLocation(mappedEvents, {
      coordinates: userCoordinates,
      locality: isManualCitySelection ? null : locationData?.locality,
      city: isManualCitySelection ? selectedCity : locationData?.city || selectedCity,
    });
    return refineResultsByIntent(
      locationSortedEvents,
      "event",
      searchIntent,
      trimmedSearchKey,
      [selectedCity, userBaseCity]
    );
  }, [events, isManualCitySelection, isNearbyQuery, locationData?.city, locationData?.locality, searchIntent, selectedCity, trimmedSearchKey, userBaseCity, userCoordinates]);

  const resultSections = useMemo(() => {
    const sectionsByType = {
      event: {
        type: "event",
        title: "Events",
        icon: "fas fa-calendar-alt mr-2",
        items: sortedEvents,
      },
      artist: {
        type: "artist",
        title: "Artists",
        icon: "fas fa-music mr-2",
        items: sortedArtists,
      },
      club: {
        type: "club",
        title: "Clubs",
        icon: "fas fa-glass-martini-alt mr-2",
        items: sortedClubs,
      },
    };

    return SEARCH_RESULT_DISPLAY_ORDER
      .map((type) => sectionsByType[type])
      .filter((section) => section?.items?.length);
  }, [sortedArtists, sortedClubs, sortedEvents]);

  const onPressSearchType = (type) => {
    switch (type) {
      case "Area": {
        const areaValue =
          Array.isArray(recommendation?.data?.data) &&
            recommendation?.data?.data?.length &&
            recommendation?.data?.data[0]?.localities?.length
            ? recommendation?.data?.data[0]?.localities[0]
            : "";
        applySearchQuery(areaValue);
        break;
      }
      case "Genre":
        applySearchQuery("Bollywood");
        break;
      case "Artists":
        navigate("/artists");
        break;
      case "Clubs":
        navigate("/clubs");
        break;
      default:
        break;
    }
  };

  return (
    <Layout>
      <Hero title="Search" />
      {/* Favourites List  */}
      <section
        className="hero fillter-sec section-gap"
      >
        <div className="container">
          <form
            className="location-sec mb-4"
            onSubmit={(event) => {
              event.preventDefault();
              submitSearch();
            }}
          >
            <div className="row align-items-center">
              <div className="col-lg-5 mb-md-4 mb-xl-0">
                <CityDropDown />
              </div>
              <div className="col-lg-5 mb-md-4 mb-xl-0">
                <AnimatedSearchBar
                  value={valuekey}
                  onChange={(event) => setValuekey(event.target.value)}
                  isLoading={showSearchLoader}
                />
              </div>
              <div className="col-lg-2">
                <div
                  className="fillter-button wow fadeInUp"
                  data-wow-delay="0.5s"
                >
                  <button
                    type="button"
                    className="btn"
                    onClick={() =>
                      navigate("/clubs", {
                        state: "open",
                      })
                    }
                  >
                    <i className="fa fa-cog"></i> Filter
                  </button>
                </div>
              </div>
            </div>
          </form>
          {recommendation?.data?.data && !valuekey ? (
            <>
              <div className="search-fillter-sec"></div>
              <div className="search-trending-sec">
                <div className="head-title section-gap">
                  <div className="title-sec">
                    <h2 className="title-25">Trending In Your City</h2>
                  </div>
                </div>
                <div className="trending-list d-flex flex-wrap">
                  {recommendation?.data?.data &&
                    recommendation?.data?.data?.map((item, i) => {
                      let data = [].concat(
                        item.localities,
                        item.generes,
                        item.clubs,
                        item.artists
                      );
                      return (
                        <React.Fragment key={i}>
                          {data?.map((term, idx) => (
                            <span key={`${i}-${idx}-${term}`}>
                              <a href="#" onClick={(event) => {
                                event.preventDefault();
                                applySearchQuery(term);
                              }}>
                                <i className="fas fa-angle-right"></i>
                                {term}
                              </a>
                            </span>
                          ))}
                        </React.Fragment>
                      );
                    })}

                  {trendingPrompts.map((term, idx) => (
                    <span key={`prompt-${idx}`}>
                      <a href="#" onClick={(event) => {
                        event.preventDefault();
                        applySearchQuery(term);
                      }}>
                        <i className="fas fa-angle-right"></i>
                        {term}
                      </a>
                    </span>
                  ))}
                </div>
                <div className="treading-card-list">
                  <div className="row">
                    {searchTypeImages.map((item) => (
                      <div key={item.id} className="col-lg-3 col-sm-6">
                        <a
                          href="#"
                          onClick={() => onPressSearchType(item.label)}
                        >
                          <div className="card">
                            <div className="img-sec">
                              <img
                                src={item.image}
                                alt="img"
                                className="img-fluid"
                              />
                            </div>
                          </div>
                          <p>{item.label}</p>
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : null}
          <div
            className="club-result-row section-gap-btm wow fadeInUp"
            data-wow-delay="0.2s"
          >
            {needsLocationForNearby && !locationLoading ? (
              <div className="location-retry-panel text-center section-gap py-5">
                <div className="mb-4">
                  <i className="fa fa-map-marker" style={{ fontSize: "3rem" }} aria-hidden="true" />
                </div>
                <h2 className="title-25">Live Location Required</h2>
                <p className="text-muted" style={{ maxWidth: "600px", margin: "0 auto 1.5rem" }}>
                  Nearby clubs and events need your live location. City selection alone is not enough for “near me” results.
                </p>
                {permissionState === "denied" || locationStatus === "denied" ? (
                  <p className="text-muted">
                    Location is blocked in the browser. Allow it from the address bar, then retry.
                  </p>
                ) : locationError ? (
                  <p className="text-muted">{locationError}</p>
                ) : null}
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={refreshLocation}
                >
                  <i className="fa fa-location-arrow" aria-hidden="true" /> Enable Location Access
                </button>
              </div>
            ) : null}
            {!needsLocationForNearby && !showSearchLoader && resultSections.map((section) => (
              <div className="card-list mb-4" key={section.type}>
                <div className="head-title mb-3">
                  <div className="title-sec">
                    <h2 className="title-25">
                      <i className={section.icon}></i> {section.title}
                    </h2>
                  </div>
                </div>
                <div className="row">
                  {section.items.map((item, index) => {
                    if (section.type === "event") {
                      return (
                        <Card
                          key={`event-${item?._id || index}`}
                          event={item}
                          isCard={true}
                          index={index}
                        />
                      );
                    }

                    if (section.type === "artist") {
                      return (
                        <Card
                          key={`artist-${item?._id || index}`}
                          artist={item}
                          index={index}
                        />
                      );
                    }

                    return (
                      <Card
                        key={`club-${item?._id || index}`}
                        club={item}
                        index={index}
                      />
                    );
                  })}
                </div>
              </div>
            ))}

            {!needsLocationForNearby && !showSearchLoader && trimmedSearchKey && sortedEvents.length === 0 && sortedArtists.length === 0 && sortedClubs.length === 0 && (
              <div className="no-data-found text-center section-gap py-5">
                <div className="mb-4">
                  <i className="fas fa-search" style={{ fontSize: '4rem', color: '#ccc' }}></i>
                </div>
                <h2 className="title-25">No results found</h2>
                <p className="text-muted" style={{ maxWidth: '600px', margin: '0 auto' }}>
                  We couldn't find any events, artists, or clubs matching "<strong>{trimmedSearchKey}</strong>". <br />
                  Try adjusting your search keywords or explore our trending categories.
                </p>
                
              <div className="search-fillter-sec"></div>
              <div className="search-trending-sec">
                <div className="head-title section-gap">
                  <div className="title-sec">
                    <h2 className="title-25">Trending In Your City</h2>
                  </div>
                </div>
                <div className="trending-list d-flex flex-wrap">
                  {recommendation?.data?.data &&
                    recommendation?.data?.data?.map((item, i) => {
                      let data = [].concat(
                        item.localities,
                        item.generes,
                        item.clubs,
                        item.artists
                      );
                      return (
                        <React.Fragment key={i}>
                          {data?.map((term, idx) => (
                            <span key={`${i}-${idx}-${term}`}>
                              <a href="#" onClick={(event) => {
                                event.preventDefault();
                                applySearchQuery(term);
                              }}>
                                <i className="fas fa-angle-right"></i>
                                {term}
                              </a>
                            </span>
                          ))}
                        </React.Fragment>
                      );
                    })}

                  {trendingPrompts.map((term, idx) => (
                    <span key={`prompt-${idx}`}>
                      <a href="#" onClick={(event) => {
                        event.preventDefault();
                        applySearchQuery(term);
                      }}>
                        <i className="fas fa-angle-right"></i>
                        {term}
                      </a>
                    </span>
                  ))}
                </div>
                <div className="treading-card-list">
                  <div className="row">
                    {searchTypeImages.map((item) => (
                      <div key={item.id} className="col-lg-3 col-sm-6">
                        <a
                          href="#"
                          onClick={() => onPressSearchType(item.label)}
                        >
                          <div className="card">
                            <div className="img-sec">
                              <img
                                src={item.image}
                                alt="img"
                                className="img-fluid"
                              />
                            </div>
                          </div>
                          <p>{item.label}</p>
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
           
              </div>

              
            )}

              {showSearchLoader && <LoadingSpinner />}
            
          </div>
        </div>
      </section>
      {/* Favourites List End  */}
      <a href="#" className="back-to-top">
        <i className="fa fa-chevron-up"></i>
      </a>
      <ToastContainer />
    </Layout>
  );
};

export default Search;
